import { Badge } from "@/components/ui/badge";
import type { DealStatus, PortalDealRow } from "@colab/shared";

// Single source of truth for status presentation across the deals list page
// (deal cards) and the detail page (header). For source='deal' (internal
// status), uses the DealStatus enum. For source='attribution' (HubSpot
// stage-driven), renders the stage label and a stable variant.
//
// Withdrawal: when a row has `is_withdrawn=true`, override the label to
// "Withdrawn" — the underlying status is `closed_lost` (soft state per
// services/deals.ts WITHDRAW_MARKER) but the partner thinks of it as a
// distinct outcome.

type Variant = "default" | "secondary" | "destructive" | "outline";

const INTERNAL_MAP: Record<DealStatus, { variant: Variant; label: string }> = {
  submitted: { variant: "outline", label: "Submitted" },
  under_review: { variant: "outline", label: "Under review" },
  approved: { variant: "default", label: "Approved" },
  active: { variant: "default", label: "Active" },
  closed_won: { variant: "secondary", label: "Closed - won" },
  closed_lost: { variant: "destructive", label: "Closed - lost" },
};

type Props =
  | { row: PortalDealRow }
  | { status: DealStatus; isWithdrawn?: boolean }
  | { stageLabel: string };

export function DealStatusBadge(props: Props) {
  // From a unified row.
  if ("row" in props) {
    const { row } = props;
    if (row.is_withdrawn) {
      return <Badge variant="secondary">Withdrawn</Badge>;
    }
    if (row.source === "deal" && row.status) {
      const entry = INTERNAL_MAP[row.status];
      return <Badge variant={entry.variant}>{entry.label}</Badge>;
    }
    if (row.source === "attribution") {
      const label = row.stage_label ?? row.stage_id ?? "—";
      const variant: Variant =
        row.is_closed && row.stage_id === "149676061"
          ? "secondary"
          : row.is_closed
            ? "destructive"
            : "default";
      return <Badge variant={variant}>{label}</Badge>;
    }
    return <Badge variant="outline">—</Badge>;
  }

  if ("status" in props) {
    if (props.isWithdrawn) {
      return <Badge variant="secondary">Withdrawn</Badge>;
    }
    const entry = INTERNAL_MAP[props.status];
    return <Badge variant={entry.variant}>{entry.label}</Badge>;
  }

  return <Badge variant="default">{props.stageLabel}</Badge>;
}
